import React from 'react';
import {
  Box,
  Container,
  Skeleton,
  Grid,
  Paper,
  Stack,
  useTheme,
  useMediaQuery,
  Divider,
} from '@mui/material';
import { styled } from '@mui/material/styles';

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  height: '100%',
  backgroundColor: theme.palette.background.paper,
}));

const LoadingSkeleton = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  
  return (
    <Container maxWidth="xlg" sx={{ py: 3 }}>
      {/* Header with avatar and tabs */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 4 }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Skeleton variant="circular" width={50} height={50} animation="pulse" />
          <Box>
            <Skeleton variant="text" width={150} height={30} animation="pulse" />
            <Skeleton variant="text" width={200} height={20} animation="pulse" />
          </Box>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Skeleton variant="rounded" width={80} height={36} animation="pulse" />
          <Skeleton variant="rounded" width={80} height={36} animation="pulse" />
        </Stack>
      </Stack>

      {/* Main stats cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StyledPaper>
            <Skeleton variant="text" width={100} height={24} />
            <Skeleton variant="text" width={60} height={48} />
            <Skeleton variant="text" width={80} height={20} />
          </StyledPaper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledPaper>
            <Skeleton variant="text" width={100} height={24} />
            <Skeleton variant="text" width={60} height={48} />
            <Skeleton variant="text" width={80} height={20} />
          </StyledPaper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledPaper>
            <Skeleton variant="text" width={100} height={24} />
            <Skeleton variant="text" width={60} height={48} />
            <Skeleton variant="text" width={80} height={20} />
          </StyledPaper>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StyledPaper>
            <Skeleton variant="text" width={100} height={24} />
            <Skeleton variant="text" width={60} height={48} />
            <Skeleton variant="text" width={80} height={20} />
          </StyledPaper>
        </Grid>
      </Grid>

      {/* Profile completion warning */}
      <StyledPaper sx={{ mb: 4, bgcolor: 'grey.100' }}>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={2} alignItems="center">
          <Skeleton variant="circular" width={40} height={40} />
          <Box flex={1}>
            <Skeleton variant="text" width="80%" height={24} />
            <Skeleton variant="text" width="60%" height={20} />
          </Box>
          <Skeleton variant="rounded" width={100} height={36} />
        </Stack>
      </StyledPaper>

      {/* Cards section */}
      <Box sx={{ mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Skeleton variant="text" width={120} height={32} />
          <Skeleton variant="text" width={80} height={24} />
        </Stack>
        
        <Grid container spacing={2}>
          {[1, 2, 3].map((item) => (
            <Grid item xs={12} sm={6} md={4} key={item}>
              <StyledPaper>
                <Skeleton variant="rounded" width="100%" height={isMobile ? 160 : 200} />
                <Box sx={{ mt: 2 }}>
                  <Skeleton variant="text" width="70%" />
                  <Skeleton variant="text" width="50%" />
                </Box>
              </StyledPaper>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Tools section */}
      <Box sx={{ mb: 4 }}>
        <Skeleton variant="text" width={100} height={32} sx={{ mb: 2 }} />
        <Grid container spacing={2}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Grid item xs={4} sm={3} md={2} key={item}>
              <Box sx={{ textAlign: 'center' }}>
                <Skeleton variant="circular" width={60} height={60} sx={{ mx: 'auto', mb: 1 }} />
                <Skeleton variant="text" width={80} sx={{ mx: 'auto' }} />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Services section */}
      <Box>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
          <Skeleton variant="text" width={120} height={32} />
          <Skeleton variant="text" width={60} height={24} />
        </Stack>
        
        <Grid container spacing={2}>
          {[1, 2, 3, 4].map((item) => (
            <Grid item xs={6} sm={3} key={item}>
              <StyledPaper sx={{ textAlign: 'center' }}>
                <Skeleton variant="circular" width={50} height={50} sx={{ mx: 'auto', mb: 1 }} />
                <Skeleton variant="text" width="70%" sx={{ mx: 'auto' }} />
                <Skeleton variant="text" width="50%" sx={{ mx: 'auto' }} />
              </StyledPaper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
};

export default LoadingSkeleton;